import { useComputed } from "@homebound/beam";
import { useCallback } from "react";
import { FieldState, ObjectState } from "src/utils/formState";
import { AiFieldSourcesProps, pendingSources } from "../AiProposalValue";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";
import { pendingProposedValue, resolveAiProposalStatus } from "./bindAiProposal";

/**
 * Shows AI's proposed value for a new entity it created, and can `resolve` it with the user's preferred value. Its
 * `sources` are for the field's `helperText`, as the `BoundAi*` fields cite theirs.
 */
export function useAiCreatedValueProposal<V>(
  field: FieldState<V | null | undefined>,
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined,
): { proposedValue: V | undefined; sources: AiFieldSourcesProps | undefined; resolve: (value: V | undefined) => void } {
  // Since this is a newly-created entity, the originalValue will have the proposed value in it already (unlike edit proposals, which keep their current value & the proposed value is in the separate AFP)
  const parser = useCallback(() => field.originalValue ?? undefined, [field]);
  const proposedValue = useComputed(() => pendingProposedValue(aiProposal, parser), [aiProposal, parser]);
  const sources = useComputed(() => pendingSources(aiProposal), [aiProposal]);
  const resolve = useCallback(
    (value: V | undefined) => resolveAiProposalStatus(aiProposal, parser, value),
    [aiProposal, parser],
  );
  return { proposedValue, sources, resolve };
}
